import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import type { TaskSignal, TodosResponse } from '@indipia/shared';
import { getSignals, getTodayTodos } from '../api/signalApi';
import { SHIP_QUERY_PREFIX } from '../constants/ship.logic';

/** 신호 쿼리 키 접두. 반장 배정·발송·확정 변경·날짜 저장 성공 지점이 invalidateQueries({ queryKey: SIGNALS_KEY }) 로 갱신한다. */
export const SIGNALS_KEY = ['signals'] as const;

/**
 * 현장의 활성 스케줄 공종별 신호(반장·발주·작업) 조회.
 * 항상 최신: staleTime 0 · 마운트마다 재조회 · 창 포커스 재조회. 오류는 던져서 isError 로 노출한다 —
 * 빈 결과(SIGNAL_MISSING 등)를 폴백으로 덮지 않으며, 호출 화면이 오류 상태를 명시적으로 보여준다.
 * 두 마운트(스케줄러 페이지·스튜디오 드로어)가 같은 키를 공유한다.
 */
export function useTaskSignals(siteId: number | null | undefined, options: { enabled?: boolean } = {}) {
  const user = useAuthStore(s => s.user);
  const compId = user?.compId;
  return useQuery<TaskSignal[]>({
    queryKey: ['signals', siteId],
    queryFn: () => getSignals(siteId as number, { headers: compId ? { 'X-Comp-Id': String(compId) } : undefined }),
    enabled: siteId != null && (options.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * 신호가 바뀔 수 있는 변경(반장 배정·발송·확정 변경·날짜 저장) 성공 직후 호출 — 신호 쿼리를 무효화한다.
 * 신호에서 파생되는 셸의 나의 할일(['todos'])·현장 신호(['site-signals'])도 함께 무효화한다(쿼리 키 접두 기준, 셸 코드를 import 하지 않음).
 */
export function useInvalidateSignals() {
  const qc = useQueryClient();
  return useCallback(
    () => Promise.all([
      qc.invalidateQueries({ queryKey: SIGNALS_KEY }),
      qc.invalidateQueries({ queryKey: ['todos'] }),
      qc.invalidateQueries({ queryKey: ['site-signals'] }),
      qc.invalidateQueries({ queryKey: SHIP_QUERY_PREFIX }),
    ]),
    [qc],
  );
}

/**
 * 오늘 할일(빨강·노랑 + 할일 번호) 조회 — [오늘 할일 N] 건수·패널의 원천. 셸 나의 할일과 같은 쿼리 키 모양(['todos', { scope, siteId, owner }]).
 * siteId 를 주면 그 현장만, 생략하면 호출자의 모든 현장(A홈). 항상 최신, 오류는 isError 로 노출(폴백 없음).
 */
export function useTodayTodos(siteId?: number | null, options: { enabled?: boolean } = {}) {
  const user = useAuthStore(s => s.user)
  const headers = {
    ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }
  const sid = siteId ?? undefined
  return useQuery<TodosResponse>({
    queryKey: ['todos', { scope: 'today', siteId: sid, owner: undefined }],
    queryFn: () => getTodayTodos({ siteId: sid, headers }),
    enabled: options.enabled ?? true,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
}
